import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MovePayload } from 'pages/NestedCustomers/types'
import { SourceWalletTypes } from 'types/move/move'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { roundNumberWithCommas } from 'utils/utils'
import { useSourceWallet } from './useSourceWallet'

interface InternalTransferDetails {
  label: string
  description: string
  testid: string
}

export const useInternalTransferDetails = (formValues: MovePayload): InternalTransferDetails[] => {
  const { t } = useTranslation()
  const sourceWallet = useSourceWallet()

  const details: InternalTransferDetails[] = useMemo(() => {
    const isCustomerWalletSource = sourceWallet.walletType === SourceWalletTypes.CUSTOMER

    const amountDetails = {
      label: t('customers.paymentAmount'),
      description: `${roundNumberWithCommas(formValues?.amount)} ${sourceWallet?.currency}`,
      testid: 'transfer-amount',
    }

    const fromWalletDetails = {
      label: t(`customers.${isCustomerWalletSource ? 'fromCustomerWallet' : 'fromMainWallet'}`),
      description: sourceWallet?.walletName,
      testid: 'source-wallet',
    }

    const paymentReferenceDetails = {
      label: t('massPayouts.paymentReference'),
      description: formValues?.paymentReference,
      testid: 'payment-reference',
    }

    const toWalletDetails = [
      ...(formValues?.walletTransferType === WalletTransferType.CUSTOMER
        ? [
            {
              label: t('customers.customer'),
              description: formValues?.destinationCustomerName,
              testid: 'transfer-customer',
            },
            {
              label: t('customers.toCustomerWallet'),
              description: formValues?.destinationWalletName,
              testid: 'customer-wallet',
            },
          ]
        : []),
      ...(formValues?.walletTransferType === WalletTransferType.MAIN
        ? [
            {
              label: t('customers.toWallet'),
              description: formValues?.destinationWalletName,
              testid: 'main-wallet',
            },
          ]
        : []),
    ]

    return [
      amountDetails,
      fromWalletDetails,
      ...toWalletDetails,
      paymentReferenceDetails,
    ] as InternalTransferDetails[]
  }, [sourceWallet, formValues])

  return details
}
